import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type React from "react";

import { FitRoom, Pager, packPages, useRoomBelow } from "../ui/paging";
import type { PageRange } from "../ui/paging";
import styles from "./accounting.module.css";

/** The compact pager under the parts, and a little air. */
const PAGER_ROOM = 48;

/**
 * **A report tab, a page of whole parts at a time** (Emile, 2026-10-17: *"please
 * always calculate to fit screen size … for any screen size, not just in the
 * prototype but in the real code as well"*). Accounting's tabs were long reports
 * — figures, then charts, then tables — running 300–1,200px past a laptop's
 * window. Every part is kept; they are packed into pages that fit the room left
 * to the bottom of the content area, with a pager under them.
 *
 * **It works on the drawn parts, not on the tab's code**: the parts are the
 * children of the first element that has more than one, measured as drawn and
 * then hidden or shown by page. So no tab had to be rewritten, and a tab that
 * gains a part is paged with it. A part taller than the whole room has a page
 * to itself.
 */
export function FitSections({
  children,
  resetOn,
}: {
  children: React.ReactNode;
  /** Back to the first page when this changes: another tab, year or month. */
  resetOn: unknown;
}): React.JSX.Element {
  const box = useRef<HTMLDivElement | null>(null);
  const [roomRef, room] = useRoomBelow(PAGER_ROOM);
  const [pages, setPages] = useState<PageRange[]>([[0, Number.MAX_SAFE_INTEGER]]);
  const [page, setPage] = useState(0);
  const [tick, setTick] = useState(0);
  /** The room a part may take: the whole room, less a tab's controls kept on every page. */
  const [partRoom, setPartRoom] = useState(0);

  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      box.current = element;
      roomRef(element);
    },
    [roomRef],
  );

  useEffect(() => {
    setPage(0);
  }, [resetOn]);

  /** The parts: the children of the first element down that has more than one. */
  const parts = (): HTMLElement[] => {
    let host: Element | null = box.current;
    while (host !== null && host.children.length === 1) host = host.firstElementChild;
    return host === null ? [] : ([...host.children] as HTMLElement[]);
  };

  // Measure every part as drawn, pack them into pages, then show one page.
  useLayoutEffect(() => {
    const every = parts();
    for (const part of every) part.style.display = "";
    // A tab's controls (year, month, location) stay on every page, above it.
    const pinned = every.filter((part) => /controls/.test(String(part.className)));
    const all = every.filter((part) => !pinned.includes(part));
    if (room <= 0 || all.length === 0) return;
    const pinnedPx = pinned.reduce(
      (sum, part) => sum + part.getBoundingClientRect().height + 14,
      0,
    );
    const budget = Math.max(120, room - pinnedPx);
    setPartRoom((was) => (Math.abs(was - budget) > 2 ? budget : was));
    const tops = all.map((part) => part.getBoundingClientRect().top);
    const last = all[all.length - 1];
    const bottom = last === undefined ? 0 : last.getBoundingClientRect().bottom;
    // Each part's height includes the gap after it, so a page packs what is drawn.
    const heights = all.map((_, i) => Math.ceil((tops[i + 1] ?? bottom) - (tops[i] ?? 0)));
    const packed = packPages(heights, budget, 0);
    setPages((was) => (JSON.stringify(was) === JSON.stringify(packed) ? was : packed));
    const current = packed[Math.min(page, packed.length - 1)] ?? [0, all.length];
    all.forEach((part, i) => {
      part.style.display = i >= current[0] && i < current[1] ? "" : "none";
    });
  });

  // Re-measure when the parts change inside a tab (data arriving, a chart drawn)
  // or the window does; a part's own re-render does not re-render this.
  useEffect(() => {
    const element = box.current;
    if (element === null) return undefined;
    let frame = 0;
    const schedule = (): void => {
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setTick((t) => t + 1);
      });
    };
    const watcher = new MutationObserver(schedule);
    watcher.observe(element, { childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", schedule);
    return () => {
      watcher.disconnect();
      window.removeEventListener("resize", schedule);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, []);
  void tick;

  const count = pages.length;
  const current = Math.min(page, count - 1);
  const range = pages[current] ?? [0, 0];
  const total = pages.at(-1)?.[1] ?? 0;

  return (
    <>
      <div className={styles.fitSections} ref={attach}>
        <FitRoom.Provider value={partRoom > 0 ? partRoom : null}>{children}</FitRoom.Provider>
      </div>
      <Pager
        compact
        page={current}
        count={count}
        onPage={setPage}
        from={range[0] + 1}
        to={range[1]}
        total={total}
        noun="parts of this report"
        testId="accounting-pager"
      />
    </>
  );
}
